import { BRIDGE_SEATS, type BridgeSeat, cardId, pirateLadder, pirateViewFor, sortHand } from '@chessmate/game-engine';
import { CardBack, PlayingCard, SUIT_SYMBOL, CARD_SLOT_CLASS, HAND_CLASS } from '../components/PlayingCard.js';
import { SurfaceTheme } from '../components/SurfaceTheme.js';
import { CardTable } from '../components/table/CardTable.js';
import { RoundSummaryPanel, type RoundSummary } from '../components/table/RoundSummaryPanel.js';
import { ScoreStrip } from '../components/table/ScoreStrip.js';
import { SeatPlate } from '../components/table/SeatPlate.js';
import { TrickArea } from '../components/table/TrickArea.js';
import { useSeatIdentity } from '../components/table/useSeatIdentity.js';
import { useT } from '../i18n/useT.js';
import { HUMAN_SEAT, usePirateStore } from '../state/pirate-store.js';
import { useSeatBubbles } from '../state/table-chatter.js';

/**
 * The Pirate Bridge screen: you in South, three robots, everyone for themselves. It renders only from `pirateViewFor(round, HUMAN_SEAT)`,
 * so a hand or a guess you may not see is never in the data this component can reach. In a Cap'n's Hat round your own card is not in
 * that view at all; the opponents' cards are, face up. Rules: docs/proposals/whist-and-pirate-bridge.md.
 */
export function PirateScreen({ onExit }: { onExit: () => void }) {
  const { t } = useT('games');
  const game = usePirateStore((s) => s.game);
  const round = usePirateStore((s) => s.round);
  const result = usePirateStore((s) => s.result);
  const roundNumber = usePirateStore((s) => s.roundNumber);
  const winners = usePirateStore((s) => s.winners);
  const draftGuess = usePirateStore((s) => s.draftGuess);
  const seed = usePirateStore((s) => s.seed);
  const strength = usePirateStore((s) => s.strength);
  const fourColour = usePirateStore((s) => s.fourColour);
  const bigCards = usePirateStore((s) => s.bigCards);
  const hat = usePirateStore((s) => s.hat);
  const raceTarget = usePirateStore((s) => s.raceTarget);
  const setRaceTarget = usePirateStore((s) => s.setRaceTarget);
  const tournament = usePirateStore((s) => s.tournament);
  const endTournament = usePirateStore((s) => s.endTournament);
  const seatNames = usePirateStore((s) => s.seatNames);
  const setSeatNames = usePirateStore((s) => s.setSeatNames);
  const setDraftGuess = usePirateStore((s) => s.setDraftGuess);
  const lockGuess = usePirateStore((s) => s.lockGuess);
  const play = usePirateStore((s) => s.play);
  const playHat = usePirateStore((s) => s.playHat);
  const nextRound = usePirateStore((s) => s.nextRound);
  const newGame = usePirateStore((s) => s.newGame);
  const setStrength = usePirateStore((s) => s.setStrength);
  const setFourColour = usePirateStore((s) => s.setFourColour);
  const setBigCards = usePirateStore((s) => s.setBigCards);
  const setHat = usePirateStore((s) => s.setHat);

  const identify = useSeatIdentity(BRIDGE_SEATS, (x) => t(`bridge-seat-${x}`));
  const bubbles = useSeatBubbles();
  if (!game || !round) return null;
  const view = pirateViewFor(round, HUMAN_SEAT);
  const seatName = (s: BridgeSeat) => t(`cardgame-seat-${s}`);
  const suitName = `${SUIT_SYMBOL[view.trump]} ${t(`bridge-suit-${view.trump}`)}`;
  const legal = new Set(view.legalPlays.map(cardId));
  const rounds = pirateLadder(game.minRank).length;
  const guessing = view.phase === 'guess' && view.ownGuess === null;
  const list = (by: Readonly<Record<BridgeSeat, number>>) => BRIDGE_SEATS.map((s) => `${seatName(s)} ${by[s]}`).join(' · ');

  let status: string;
  if (view.phase === 'done' && result) {
    const exact = BRIDGE_SEATS.filter((s) => result[s].exact);
    status = exact.length === 0 ? t('pirate-round-nobody') : t('pirate-round-exact', { seats: exact.map(seatName).join(', ') });
  } else if (guessing) status = t('pirate-guess-prompt');
  else if (view.phase === 'guess') status = t('pirate-guess-waiting');
  else if (view.canAct) status = view.hat ? t('pirate-turn-hat') : t('pirate-turn-you');
  else status = t('pirate-turn-wait', { seat: seatName(view.turn) });

  const lastTrick = view.tricks[view.tricks.length - 1];
  const shown = view.trick.length > 0 ? view.trick : (lastTrick?.plays ?? []);
  const trickWinner = view.trick.length === 0 ? lastTrick?.winner : undefined;

  const guessLine = (seat: BridgeSeat) =>
    view.guesses ? `${view.guesses[seat]}` : view.hasGuessed[seat] ? t('pirate-guess-hidden') : '…';

  const seatHand = (seat: BridgeSeat) => {
    const hatCard = view.hatCards?.[seat];
    return (
      <section aria-label={t('bridge-hand-of', { seat: seatName(seat) })} data-testid={`pirate-hand-${seat}`} className="flex flex-col items-center gap-1">
        <SeatPlate
          bubble={bubbles[seat]?.line}
          testid={`pirate-seat-${seat}`}
          name={identify(seat).name}
          tag={identify(seat).tag}
          portrait={identify(seat).portrait}
          fallbackIcon={identify(seat).fallbackIcon}
          you={identify(seat).you}
          active={view.turn === seat && !result}
        />
        <p className="text-sm font-bold text-fg-muted lg:text-base">
          <span data-testid={`pirate-guess-${seat}`}>{guessLine(seat)}</span>
          {view.guesses && <span data-testid={`pirate-won-${seat}`}> / {view.tricksWon[seat]}</span>}
        </p>
        {seat === HUMAN_SEAT ? (
          view.hat ? (
            <div className="flex items-center gap-2" data-testid="pirate-hat-own">
              <CardBack />
              <span className="text-sm font-bold text-fg-muted">{t('pirate-hat-yours')}</span>
            </div>
          ) : (
            <div className={HAND_CLASS}>
              {sortHand(view.hand).map((c) => (
                <PlayingCard key={cardId(c)} card={c} fourColour={fourColour} onPlay={legal.has(cardId(c)) ? () => play(c) : undefined} dimmed={view.canAct && !legal.has(cardId(c))} />
              ))}
            </div>
          )
        ) : hatCard ? (
          <div data-testid={`pirate-hat-card-${seat}`}>
            <PlayingCard card={hatCard} fourColour={fourColour} />
          </div>
        ) : (
          <div className="flex items-center gap-1" aria-label={t('bridge-cards-left', { n: view.handSizes[seat] })}>
            <CardBack />
            <span className="text-sm font-bold text-fg-muted">× {view.handSizes[seat]}</span>
          </div>
        )}
      </section>
    );
  };

  const highest = Math.max(...BRIDGE_SEATS.map((x) => game.totals[x]));
  const summary: RoundSummary | null = result
    ? {
        round: roundNumber,
        outcome: winners
          ? winners.includes(HUMAN_SEAT) ? 'win' : 'lose'
          : result[HUMAN_SEAT].exact ? 'win' : 'neutral',
        headline: winners ? (winners.includes(HUMAN_SEAT) ? (winners.length > 1 ? t('pirate-game-shared') : t('pirate-game-won')) : t('pirate-game-lost', { seats: winners.map(seatName).join(', ') })) : status,
        detail: winners ? status : undefined,
        rows: BRIDGE_SEATS.map((x) => ({
          key: x,
          label: identify(x).name,
          portrait: identify(x).portrait,
          delta: t('pirate-row-delta', { guess: result[x].guess, won: result[x].won, points: result[x].score, mark: result[x].exact ? '✓' : '✗' }),
          total: game.totals[x],
          you: x === HUMAN_SEAT,
          leading: highest > 0 && game.totals[x] === highest,
        })),
        gameOver: Boolean(winners),
      }
    : null;

  const slot = (seat: BridgeSeat) => {
    const p = shown.find((x) => x.seat === seat);
    return p ? <PlayingCard compact card={p.card} fourColour={fourColour} highlight={trickWinner === seat} /> : <span className={CARD_SLOT_CLASS} aria-hidden="true" />;
  };

  return (
    <SurfaceTheme>
    <div className="relative mx-auto flex w-full max-w-4xl flex-col gap-4 overflow-x-clip p-4 lg:max-w-6xl lg:gap-6 lg:p-8 xl:max-w-7xl 2xl:max-w-[96rem]" data-testid="pirate-screen" data-appdom-scope="pirate">
      <img src="/themes/pirate/cabin.webp" alt="" className="pointer-events-none absolute inset-0 size-full scale-110 object-cover opacity-25 blur-md" />
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="flex items-center gap-2 text-2xl font-extrabold text-berry">
          <img src="/themes/pirate/emblem.webp" alt="" className="size-8" data-testid="pirate-emblem" />
          {t('pirate-title')}
        </h1>
        <div className="flex items-center gap-2 text-sm font-bold">
          <span className="text-fg-muted" data-testid="pirate-seed">{t('pirate-round', { n: roundNumber, total: rounds, seed })}</span>
          <button type="button" className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2" onClick={newGame} data-testid="pirate-new-game" data-appdom-id="pirate-new-game" data-appdom-type="Button">{t('pirate-new-game')}</button>
          <button type="button" className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2" onClick={onExit} data-testid="pirate-exit" data-appdom-id="pirate-exit" data-appdom-type="Button">{t('bridge-exit')}</button>
        </div>
      </header>

      <output className="min-h-[48px] glass glass-light flex flex-col gap-1 rounded-xl px-3 py-2 text-sm font-bold text-ink" data-testid="pirate-status" data-appdom-id="pirate-status" data-appdom-type="Text" aria-live="polite">
        <span>{status}</span>
        {view.hat && (
          <span className="font-normal text-fg-muted" data-testid="pirate-hat-note">
            {t('pirate-hat-note')}
          </span>
        )}
        <span className="flex items-center gap-2 font-normal text-fg-muted" data-testid="pirate-trump">
          {t('pirate-trump', { suit: suitName })}
          <PlayingCard compact card={view.trumpCard} fourColour={fourColour} />
        </span>
        <span className="flex items-center gap-1 font-normal text-fg-muted" data-testid="pirate-scores">
          <img src="/themes/pirate/doubloon.webp" alt="" className="size-5" data-testid="pirate-doubloon" />
          {t('pirate-scores', { scores: list(game.totals) })}
        </span>
      </output>

      <ScoreStrip
        testid="pirate-score-strip"
        title={t('table-score')}
        note={t('pirate-round', { n: roundNumber, total: rounds, seed })}
        rows={BRIDGE_SEATS.map((x) => ({
          key: x,
          label: identify(x).name,
          value: game.totals[x],
          delta: result && result[x].score > 0 ? `+${result[x].score}` : undefined,
          you: x === HUMAN_SEAT,
          leading: highest > 0 && game.totals[x] === highest,
        }))}
      />

      {guessing && (
        <div className="glass glass-light flex flex-wrap items-center justify-between gap-3 rounded-xl px-3 py-2" data-testid="pirate-guess-panel" data-surface="table" data-appdom-id="pirate-guess-panel" data-appdom-type="Panel">
          <div className="flex items-center gap-2">
            <button type="button" className="h-9 w-9 rounded-lg border border-border text-lg font-extrabold disabled:opacity-40" onClick={() => setDraftGuess(draftGuess - 1)} disabled={draftGuess <= 0} aria-label={t('pirate-guess-less')} data-testid="pirate-guess-less">−</button>
            <output className="min-w-8 text-center text-2xl font-extrabold text-ink" data-testid="pirate-guess-value">{draftGuess}</output>
            <button type="button" className="h-9 w-9 rounded-lg border border-border text-lg font-extrabold disabled:opacity-40" onClick={() => setDraftGuess(draftGuess + 1)} disabled={draftGuess >= view.cardsPerHand} aria-label={t('pirate-guess-more')} data-testid="pirate-guess-more">+</button>
          </div>
          <button type="button" onClick={lockGuess} data-testid="pirate-guess-lock" className="min-h-[48px] rounded-xl bg-accent px-4 py-2 font-display font-semibold text-accent-fg">
            {t('pirate-guess-lock', { n: draftGuess })}
          </button>
        </div>
      )}

      {view.mustPlayHidden && (
        <div className="glass glass-light flex flex-wrap items-center justify-between gap-2 rounded-xl px-3 py-2" data-testid="pirate-hat-panel" data-surface="table" data-appdom-id="pirate-hat-panel" data-appdom-type="Panel">
          <span className="text-sm font-bold text-ink">{t('pirate-hat-title')}</span>
          <button type="button" onClick={playHat} data-testid="pirate-hat-play" className="min-h-[48px] rounded-xl bg-accent px-4 py-2 font-display font-semibold text-accent-fg">
            {t('pirate-hat-play')}
          </button>
        </div>
      )}

      <CardTable
        top={seatHand('N')}
        left={seatHand('W')}
        right={seatHand('E')}
        center={<TrickArea testid="pirate-trick" label={t('bridge-trick-area')} north={slot('N')} west={slot('W')} east={slot('E')} south={slot('S')} />}
        bottom={seatHand('S')}
      />

      {result && summary && (
        <RoundSummaryPanel
          testid="pirate-result"
          summary={summary}
          nextLabel={winners ? t('pirate-new-game') : t('pirate-next-round')}
          onNext={winners ? newGame : nextRound}
          nextTestid={winners ? 'pirate-play-again' : 'pirate-next-round'}
          headlineTestid="pirate-game-over"
        />
      )}

      
        {tournament && (
          <div className="glass glass-light rounded-xl px-3 py-2 text-sm" data-testid="pirate-tournament">
            <div className="flex items-center justify-between gap-2">
              <span className="font-bold">{t('pirate-tournament-title')}</span>
              <button type="button" className="min-h-[48px] rounded-lg px-2 py-1 text-fg-muted underline" onClick={endTournament} data-testid="pirate-tournament-end">
                {t('pirate-tournament-end')}
              </button>
            </div>
            {tournament.session.games === 0 && (
              <div className="mt-1 flex flex-wrap items-center gap-2" data-testid="pirate-tournament-names">
                {(['N', 'E', 'W'] as const).map((seat) => (
                  <label key={seat} className="flex items-center gap-1 text-xs text-fg-muted">
                    {seat}
                    <input
                      type="text"
                      value={seatNames[seat] ?? ''}
                      onChange={(e) => setSeatNames({ ...seatNames, [seat]: e.target.value })}
                      placeholder={t('pirate-seat-name-for', { seat })}
                      className="w-24 rounded-lg border border-border px-2 py-1 text-sm text-fg"
                      data-testid={`pirate-seat-name-${seat}`}
                    />
                  </label>
                ))}
              </div>
            )}
            <p>
              {t('pirate-tournament-session')}: {(['S', 'N', 'E', 'W'] as const).map((seat) => `${seat === 'S' ? t('pirate-you') : seatNames[seat] ?? seat} ${tournament.session.wins[seat]}`).join(' · ')}
              {' '}
              ({tournament.session.games}) · {t('pirate-tournament-series')} {tournament.series.wins.S}
            </p>
          </div>
        )}


      <details className="rounded-2xl border border-border bg-surface p-3" data-testid="pirate-settings">
        <summary className="flex min-h-[48px] cursor-pointer items-center text-sm font-extrabold uppercase tracking-wide text-fg-muted">{t('bridge-robots')}</summary>
        <div className="mt-3 flex flex-col gap-3 text-sm font-bold">
          <div className="flex items-center gap-2">
            {(['easy', 'clever'] as const).map((s) => (
              <button key={s} type="button" aria-pressed={strength === s} data-testid={`pirate-strength-${s}`} onClick={() => setStrength(s)} className={`min-h-[48px] rounded-lg border px-3 py-1 ${strength === s ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}>
                {t(`bridge-${s}`)}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={bigCards} onChange={(e) => setBigCards(e.target.checked)} data-testid="pirate-big-cards" />
            {t('big-cards')}
          </label>
          <div className="flex items-center gap-2" role="group" aria-label={t('pirate-race-title')} data-testid="pirate-race">
            <span className="text-sm text-fg-muted">{t('pirate-race-title')}</span>
            {([0, 100, 200] as const).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRaceTarget(n)}
                aria-pressed={raceTarget === n}
                className={`min-h-[48px] rounded-lg px-2 py-1 text-sm font-semibold ${raceTarget === n ? 'bg-accent text-accent-fg' : 'border border-border text-fg-muted'}`}
                data-testid={`pirate-race-${n === 0 ? 'ladder' : n}`}
              >
                {n === 0 ? t('pirate-race-ladder') : t('pirate-race-n', { n })}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={hat} onChange={(e) => setHat(e.target.checked)} data-testid="pirate-hat" />
            {t('pirate-hat-option')}
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={fourColour} onChange={(e) => setFourColour(e.target.checked)} data-testid="pirate-four-colour" />
            {t('bridge-four-colour')}
          </label>
          <p className="font-normal text-fg-muted">{t('pirate-teaching-note')}</p>
        </div>
      </details>
    </div>
    </SurfaceTheme>
  );
}
